'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';


interface NavigationItem {
  label: string;
  href: string;
  icon?: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Services', href: '/services-overview', icon: 'TruckIcon' },
    { label: 'Coverage', href: '/regional-coverage', icon: 'MapIcon' },
    { label: 'Safety', href: '/safety-and-compliance', icon: 'ShieldCheckIcon' },
    { label: 'About', href: '/about', icon: 'InformationCircleIcon' },
  ];

  const ctaItem: NavigationItem = {
    label: 'Get Quote',
    href: '/contact-and-quotes',
    icon: 'DocumentTextIcon',
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (href: string) => pathname === href;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        } ${className}`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              href="/homepage"
              className="flex items-center space-x-2 group"
              onClick={closeMobileMenu}
            >
              <AppImage
                src="/assets/images/2-1767736155160.png"
                alt="MylesDrive Logistics Hub Logo"
                width={180}
                height={60}
                className="h-12 lg:h-14 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`relative px-4 py-2 font-body font-semibold text-sm transition-all duration-300 rounded-md group ${
                    isActive(item.href)
                      ? 'text-primary' :'text-muted-foreground hover:text-primary'
                  }`}
                >
                  <span className="relative z-10">{item.label}</span>
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-full" />
                  )}
                  <span className="absolute inset-0 bg-muted rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Link>
              ))}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                href={ctaItem.href}
                className="relative px-6 py-2.5 font-cta font-semibold text-sm text-primary bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-md shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5 overflow-hidden group"
              >
                <span className="relative z-10 flex items-center space-x-2">
                  <span>{ctaItem.label}</span>
                  <Icon name="ArrowRightIcon" size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-primary hover:bg-muted transition-colors duration-300"
              aria-label="Toggle mobile menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-background z-40 lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="flex flex-col h-full overflow-y-auto">
            <div className="flex-1 px-4 py-6 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-body font-semibold text-base transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-gradient-to-r from-golden-light/10 via-golden-mid/10 to-golden-dark/10 text-primary border-l-4 border-golden-mid' :'text-muted-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  {item.icon && (
                    <Icon
                      name={item.icon as any}
                      size={20}
                      className={isActive(item.href) ? 'text-golden-mid' : ''}
                    />
                  )}
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="p-4 border-t border-border">
              <Link
                href={ctaItem.href}
                onClick={closeMobileMenu}
                className="flex items-center justify-center space-x-2 w-full px-6 py-3 font-cta font-semibold text-base text-primary bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-lg shadow-golden-md active:scale-95 transition-transform duration-300"
              >
                {ctaItem.icon && <Icon name={ctaItem.icon as any} size={20} />}
                <span>{ctaItem.label}</span>
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;